import { cx } from "@light-technology/components";
import {
  PortableText,
  toPlainText,
  type PortableTextComponents,
  type PortableTextMarkComponent,
  type PortableTextTypeComponent,
} from "@portabletext/react";
import type { ArbitraryTypedObject } from "@portabletext/types";
import { Lightbulb02 } from "@untitledui/icons";
import { toSlug } from "../../utilities/slug";

/*** Marks ***/

type LinkValue = ArbitraryTypedObject & { href?: string; blank?: boolean };

export const LinkMark: PortableTextMarkComponent<LinkValue> = ({
  value,
  children,
}) => (
  <a
    href={value?.href ?? ""}
    className="underline hover:text-primary"
    target={value?.blank ? "_blank" : undefined}
    rel={value?.blank ? "noopener noreferrer" : undefined}
  >
    {children}
  </a>
);

/*** Images ***/

type ImageValue = ArbitraryTypedObject & {
  url?: string;
  width?: number;
  height?: number;
};

const ImageBlock: PortableTextTypeComponent<ImageValue> = ({ value }) => {
  const { url, width, height } = value;
  if (!url) return null;
  return (
    <div className="mt-xl flex justify-center">
      <img
        src={url}
        alt=""
        width={width}
        height={height}
        className="max-h-[75vh] w-auto max-w-full rounded-lg object-contain"
      />
    </div>
  );
};

/*** Callouts ***/

type CalloutTone = "info" | "warning" | "tip";

type CalloutValue = ArbitraryTypedObject & {
  tone?: CalloutTone;
  text?: string;
};

const CALLOUTS: Record<
  CalloutTone,
  {
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    iconColor: string;
    styles: string;
  }
> = {
  tip: {
    icon: Lightbulb02,
    iconColor: "text-success-primary",
    styles: cx("border-success-200 bg-success-primary text-success-700"),
  },
  warning: {
    icon: Lightbulb02,
    iconColor: "text-warning-500",
    styles: cx("border-warning-300 bg-warning-primary text-warning-700"),
  },
  info: {
    icon: Lightbulb02,
    iconColor: "text-gray-blue-600",
    styles: cx("border-gray-blue-200 bg-gray-blue-50 text-gray-blue-700"),
  },
};

const CalloutBlock: PortableTextTypeComponent<CalloutValue> = ({ value }) => {
  const config =
    value.tone && value.tone in CALLOUTS ? CALLOUTS[value.tone] : CALLOUTS.info;
  return (
    <div
      className={cx(
        "mt-3xl flex items-start gap-md rounded-lg border p-xl",
        config.styles,
      )}
    >
      <config.icon className={cx("size-5 shrink-0", config.iconColor)} />
      <p className="text-sm leading-6">{value.text}</p>
    </div>
  );
};

/*** Component map ***/

const portableTextComponents: PortableTextComponents = {
  block: {
    h2: ({ children, value }) => (
      <h2
        id={toSlug(toPlainText(value))}
        className="mt-6xl text-display-sm font-semibold @page-sm:text-display-md"
      >
        {children}
      </h2>
    ),
    h3: ({ children, value }) => (
      <h3
        id={toSlug(toPlainText(value))}
        className="mt-4xl text-display-xs font-semibold @page-sm:text-display-sm"
      >
        {children}
      </h3>
    ),
    h4: ({ children, value }) => (
      <h4
        id={toSlug(toPlainText(value))}
        className="text-display-2xs mt-2xl font-semibold @page-sm:text-display-xs"
      >
        {children}
      </h4>
    ),
    normal: ({ children }) => <p className="mt-xl text-md">{children}</p>,
  },
  list: {
    bullet: ({ children }) => (
      <ul className="mt-xl list-disc pl-xl text-md">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="mt-xl list-decimal pl-xl text-md">{children}</ol>
    ),
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold">{children}</strong>
    ),
    em: ({ children }) => <em>{children}</em>,
    code: ({ children }) => (
      <code className="rounded bg-secondary px-xs font-mono text-xs">
        {children}
      </code>
    ),
    link: LinkMark,
  },
  types: {
    image: ImageBlock,
    callout: CalloutBlock,
  },
};

export const PlaybookBody = ({ body }: { body: unknown[] }) => {
  // TODO: Consider stronger runtime type check
  if (!Array.isArray(body)) {
    return null;
  }
  return (
    <div className="*:first:mt-0">
      <PortableText value={body} components={portableTextComponents} />
    </div>
  );
};
